// Requisiti della password dell'Area Clienti, configurati dal tenant.
// Le regex devono restare identiche a quelle di
// Modules/Portal/Services/PortalPasswordPolicy.php: il controllo vero e' sul
// server, qui servono solo i messaggi e la checklist mentre si digita.

export const DEFAULT_PORTAL_PASSWORD_POLICY = {
	min_length: 8,
	uppercase: true,
	lowercase: false,
	numbers: true,
	symbols: false
}

const PATTERNS = [
	{ key: 'uppercase', label: 'Una lettera maiuscola', regex: /[A-Z]/ },
	{ key: 'lowercase', label: 'Una lettera minuscola', regex: /[a-z]/ },
	{ key: 'numbers', label: 'Un numero', regex: /[0-9]/ },
	{ key: 'symbols', label: 'Un simbolo (es. ! ? @ #)', regex: /[^A-Za-z0-9]/ }
]

export function passwordRequirements(policy) {
	const p = policy || DEFAULT_PORTAL_PASSWORD_POLICY
	const requirements = [{
		key: 'min_length',
		label: 'Almeno ' + p.min_length + ' caratteri',
		// Array.from conta i caratteri come mb_strlen sul server: un'emoji vale 1, non 2
		test: v => !!v && Array.from(v).length >= p.min_length
	}]
	PATTERNS.forEach(pattern => {
		if (p[pattern.key]) {
			requirements.push({
				key: pattern.key,
				label: pattern.label,
				test: v => !!v && pattern.regex.test(v)
			})
		}
	})
	return requirements
}

export function buildPasswordRules(policy) {
	return [v => !!v || 'Password obbligatoria'].concat(
		passwordRequirements(policy).map(r => v => r.test(v) || r.label)
	)
}

// Con un backend precedente l'endpoint non esiste: si resta sui default, che
// sono le regole applicate prima che la policy fosse configurabile.
export async function fetchPortalPasswordPolicy(axios) {
	try {
		const resp = await axios.get('portal/password-policy', { silent: true })
		if (resp.data && resp.data.success && resp.data.data) {
			return Object.assign({}, DEFAULT_PORTAL_PASSWORD_POLICY, resp.data.data)
		}
	} catch (e) {
		// default
	}
	return Object.assign({}, DEFAULT_PORTAL_PASSWORD_POLICY)
}

// Stima di quanto sono sicure le regole di accesso scelte dal tenant, per
// l'indicatore nelle impostazioni. E' una stima della robustezza MINIMA che le
// regole garantiscono, non della password che ogni utente sceglie davvero.
//
// - Password: bit = lunghezza minima × log2(caratteri garantiti dalle regole).
//   Base 26 (lettere di un solo tipo); +26 solo se si chiedono sia maiuscole
//   sia minuscole; +10 numeri; +32 simboli. Punteggio da 0 a 1 fra 30 e 80 bit.
// - 2FA: e' la misura che protegge di piu' (una password indovinata non basta).
//   Senza, il livello complessivo si ferma al 75% ("Buono"); con, parte dal 60%.
const SECURITY_LEVELS = [
	{ min: 80, key: 'excellent', label: 'Ottimo', color: 'success', icon: 'mdi-shield-star', summary: 'Protezione elevata: password robuste e verifica in due passaggi.' },
	{ min: 60, key: 'good', label: 'Buono', color: 'light-green', icon: 'mdi-shield-check', summary: 'Buona protezione degli accessi.' },
	{ min: 35, key: 'fair', label: 'Sufficiente', color: 'orange', icon: 'mdi-shield-half-full', summary: 'Protezione di base: le regole si possono rafforzare.' },
	{ min: 0, key: 'weak', label: 'Debole', color: 'error', icon: 'mdi-shield-alert', summary: 'Protezione bassa: una password che rispetta queste regole è alla portata di un attacco mirato.' }
]

const PASSWORD_BITS_FLOOR = 30
const PASSWORD_BITS_TARGET = 80

export function evaluatePortalSecurity(policy, twofaRequired) {
	const p = Object.assign({}, DEFAULT_PORTAL_PASSWORD_POLICY, policy)
	// Durante la digitazione il campo puo' essere vuoto o fuori intervallo
	let minLength = parseInt(p.min_length)
	if (isNaN(minLength)) {
		minLength = DEFAULT_PORTAL_PASSWORD_POLICY.min_length
	}
	minLength = Math.min(64, Math.max(8, minLength))

	const pool = 26 + (p.uppercase && p.lowercase ? 26 : 0) + (p.numbers ? 10 : 0) + (p.symbols ? 32 : 0)
	const bits = minLength * Math.log2(pool)
	const passwordScore = Math.min(1, Math.max(0, (bits - PASSWORD_BITS_FLOOR) / (PASSWORD_BITS_TARGET - PASSWORD_BITS_FLOOR)))
	const score = Math.round(twofaRequired ? 60 + 40 * passwordScore : 75 * passwordScore)
	const level = SECURITY_LEVELS.find(l => score >= l.min)

	const classes = ['uppercase', 'lowercase', 'numbers', 'symbols'].filter(k => p[k]).length
	const factors = [
		{
			key: 'length',
			title: 'Lunghezza minima',
			detail: minLength + ' caratteri',
			status: minLength >= 14 ? 'strong' : (minLength >= 10 ? 'ok' : 'weak')
		},
		{
			key: 'classes',
			title: 'Tipi di carattere',
			detail: classes + ' su 4 richiesti',
			status: classes === 4 ? 'strong' : (classes >= 2 ? 'ok' : 'weak')
		},
		{
			key: 'twofa',
			title: 'Verifica in due passaggi',
			detail: twofaRequired ? 'Obbligatoria' : 'Non attiva',
			status: twofaRequired ? 'strong' : 'weak'
		}
	]

	// In ordine di efficacia: prima la 2FA, poi la lunghezza, poi la varieta'
	const suggestions = []
	if (level.key !== 'excellent') {
		if (!twofaRequired) {
			suggestions.push('Attiva la verifica in due passaggi: è la misura che protegge di più, e senza il livello massimo è "Buono".')
		}
		if (minLength < 12) {
			suggestions.push('Porta la lunghezza minima ad almeno 12 caratteri: la lunghezza conta più della varietà dei caratteri.')
		}
		if (classes < 3 && minLength < 16) {
			suggestions.push('Richiedi almeno tre tipi di carattere, ad esempio maiuscole, numeri e simboli.')
		}
	}

	return {
		score,
		label: level.label,
		color: level.color,
		icon: level.icon,
		summary: level.summary,
		bits: Math.round(bits),
		// Combinazioni possibili con i requisiti minimi, come potenza di 10
		combinationsExponent: Math.floor(bits * Math.log10(2)),
		factors,
		suggestions
	}
}

// Primo messaggio di validazione di una risposta 422 di Laravel.
export function firstValidationError(err, fallback) {
	const data = err && err.response && err.response.data
	if (data && data.errors) {
		const first = Object.values(data.errors)[0]
		if (Array.isArray(first) && first.length) {
			return first[0]
		}
	}
	return (data && data.message) || fallback
}
